/**
 * KAN-912: удаление нашего сообщения переписки клиента.
 * remove(message, scope): DELETE clients/<id>/messages/<id>/?scope=...;
 * по ответу сервера toast с итогом (deleteResultNotice), лента клиента
 * перечитывается (ключ ['client-messages', clientId], под ним и лента /m),
 * затем onDone. Ошибка запроса (например 409 "сообщение отправляется"):
 * toast.error с текстом сервера, onDone не зовётся.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { transcriptQueryKey } from '@/components/clients/useTranscriptMessages';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { deleteResultNotice } from '@/lib/messageDelete';
import { clientsApi } from '@/services/api/clients';
import type { DeleteClientMessageScope } from '@/types/client';

const ERROR_FALLBACK = 'Не удалось удалить сообщение';

interface DeleteVars {
  messageId: string;
  scope: DeleteClientMessageScope;
}

export interface DeleteClientMessageState {
  remove: (messageId: string, scope: DeleteClientMessageScope) => void;
  isPending: boolean;
}

export function useDeleteClientMessage(clientId: string, onDone: () => void): DeleteClientMessageState {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ messageId, scope }: DeleteVars) => clientsApi.deleteClientMessage(clientId, messageId, scope),
    onSuccess: (result, { scope }) => {
      const notice = deleteResultNotice(result, scope);
      if (notice.kind === 'success') toast.success(notice.text);
      else toast.error(notice.text);
      void queryClient.invalidateQueries({ queryKey: transcriptQueryKey(clientId, null) });
      onDone();
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, ERROR_FALLBACK));
    },
  });
  return {
    remove: (messageId, scope) => {
      if (mutation.isPending) return;
      mutation.mutate({ messageId, scope });
    },
    isPending: mutation.isPending,
  };
}
